import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from '@deliveryhero/pd-cookbook/components/ButtonV2';
import { Tag, TagVariant } from '@deliveryhero/pd-cookbook/components/Tag';
import { MenuItem } from '@mui/material';
import { useConfiguration } from 'contexts/country-config';
import { FormikHelpers, FormikProps } from 'formik';
import useAction from 'hooks/useAction';
import useApi from 'hooks/useApi';
import useNotification from 'hooks/useNotification';
import useQueryParam from 'hooks/useQueryParam/useQueryParam';
import useQueryWrapperP0 from 'hooks/useQueryWrapperP0';
import useQueryWrapperP1 from 'hooks/useQueryWrapperP1';
import LocationQueryKeys from 'query-keys/location';
import {
  Location,
  LocationAutocompleteResponse,
  LocationPayload,
  LocationState
} from 'services/api';
import { dispatchGaEvent, PageType } from 'services/google-analytics';
import { AppRoute } from 'share/types/routes';
import { AutocompleteOption } from 'src/components/Autocomplete/types';
import isFoodora from 'src/utils/is-foodora/is-foodora';
import generateQuery from 'utils/generate-query';

import ActionMenu from 'components/ActionMenu';
import CTA from 'components/CallToActions';
import LocationForm, { LocationFormValues } from 'components/Forms/Location';
import { LoadingAnimation } from 'components/LoadingAnimation';
import Page, { ActionType } from 'components/Page';
import PageHeader from 'components/PageHeader/PageHeader';
import SearchInput from 'components/SearchInput';
import FoodoraPermissions from 'components/svg/FoodoraPermissions';
import Permissions from 'components/svg/Permissions';
import TableList, { RowsPerPage, TableFilterRow } from 'components/TableList';
import { Column } from 'components/TableList/SortableHeader';

import {
  add_title,
  cta_subtitle,
  cta_title,
  deleteDialogContent,
  edit_title,
  page_subtitle,
  page_title
} from './constants';
import {
  Actions,
  ButtonText,
  DialogBtnProps,
  LocationListInfo,
  TestID
} from './types';

// locationToLocationListInfo transforms location info from API into display format
function locationToLocationListInfo(locations: Location[]): LocationListInfo[] {
  const res: LocationListInfo[] = [];
  locations.map((location: Location) => {
    res.push({
      id: location.id,
      name: location.campus,
      street_building: location.address_line1,
      city: location.city,
      zip_code: parseInt(location.post_code),
      status: location.state
    });
  });
  return res;
}

const DeleteDialogBtn = ({ onClick }: DialogBtnProps) => (
  <>
    <Button
      variant="secondary"
      onClick={() => {
        return;
      }}
    >
      Back to Locations
    </Button>
    <Button onClick={onClick} marginLeft="sm">
      Delete
    </Button>
  </>
);

export function LocationContainer() {
  const [action, setAction, { id: id_string }] = useAction<Actions>();
  const id = parseInt(id_string);
  const { pushNotification, dismissNotification } = useNotification();
  const [keepSidebarOpen, setSidebarOpen] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [addressId, setAddressId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const api = useApi();
  const navigate = useNavigate();
  const queryParam = useQueryParam();
  const queryClient = useQueryClient();
  const countryConfig = useConfiguration();

  const filters = ['search', 'count', 'offset'];

  const filtered = Object.keys(queryParam)
    .filter((key) => filters.includes(key))
    .reduce((param: Record<string, string>, key) => {
      param[key] = queryParam[key];
      return param;
    }, {});

  useEffect(() => {
    dispatchGaEvent({ pageType: PageType.LOCATION });
  }, []);

  const columns: Column<LocationListInfo>[] = useMemo(
    () => [
      {
        id: 'name',
        options: { disablePadding: false },
        label: 'Location name'
      },
      {
        id: 'street_building',
        options: { disablePadding: false },
        label: 'Street/Building'
      },
      {
        id: 'city',
        options: { disablePadding: false },
        label: 'City'
      },
      {
        id: 'zip_code',
        options: { disablePadding: false },
        label: 'Zipcode'
      },
      {
        id: 'status',
        options: { disablePadding: false },
        label: 'Status',
        format: (location) => {
          const variant: TagVariant =
            location.status === LocationState.Active
              ? TagVariant.SUCCESS
              : TagVariant.POPULAR;
          return <Tag id={variant} label={location.status} variant={variant} />;
        }
      }
    ],
    []
  );

  // location form values
  const initialValues = {
    name: '',
    street_and_building: '',
    unit_number: '',
    city: '', // `id|name`
    zipcode: '',
    building: '',
    address_other: '',
    delivery_instructions: '',
    state: LocationState.Active,
    geolocatorStreet: { id: -1, name: '' }
  };

  const location = useQueryWrapperP1(
    LocationQueryKeys.detail(id),
    async () => {
      const response = await api.company.location.get(id);
      return {
        name: response.data.campus,
        street_and_building: response.data.address_line1,
        unit_number: response.data.address_line2,
        city: `${response.data.api_city_id}|${response.data.city}`,
        zipcode: response.data.post_code,
        building: response.data.building,
        address_other: response.data.address_other,
        delivery_instructions: response.data.delivery_instructions,
        latitude: response.data.latitude,
        longitude: response.data.longitude,
        state:
          response.data.state == 'active'
            ? LocationState.Active
            : LocationState.Suspended
      };
    },
    {
      enabled: Boolean(id)
    }
  );

  // todo: handle fail request to fetch cities
  const cities = useQueryWrapperP1(
    ['cities'],
    async () => {
      const response = await api.company.location.getCities();
      return response.data;
    },
    {
      enabled: action.length !== 0
    }
  );

  const { data: addresses, isLoading: isAddressesLoading } = useQueryWrapperP1<
    LocationAutocompleteResponse,
    Error
  >(
    ['addresses', searchQuery],
    () =>
      api.geolocator.autocomplete({
        query: searchQuery,
        country: countryConfig.code
      }),
    {
      enabled: searchQuery.length > 0,
      onError: (error) => {
        pushNotification(`${error.message}`, {
          variant: 'error',
          autoHideDuration: 10000
        });
      }
    }
  );

  const handleSelectAddress = (value: AutocompleteOption) => {
    value && setAddressId(value.id as string);
  };

  const create = useMutation(async (payload: LocationPayload) => {
    return api.company.location.create(payload);
  });

  const edit = useMutation(
    (location: { id: number; payload: LocationPayload }) => {
      return api.company.location.update(location.id, location.payload);
    }
  );

  const handleClick = (type: Actions, id?: string) => {
    if (id) setAction(type, true, { id });
    else setAction(type);
  };

  const onEdit = (id: number) => {
    return () => {
      handleClick(Actions.Edit, id.toString());
    };
  };

  const handleCreate = (
    values: LocationFormValues,
    actions: FormikHelpers<LocationFormValues>
  ) => {
    const payload: LocationPayload = {
      address_id: addressId,
      campus: values.name,
      delivery_instructions: values.delivery_instructions,
      floor: values.unit_number,
      state: values.state
    };

    const loading = pushNotification(`Adding ${payload.campus}...`);
    create.mutate(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries(LocationQueryKeys.list()).then(() => {
          pushNotification(`${payload.campus} added!`, {
            variant: 'success'
          });
          setAction(Actions.Add, keepSidebarOpen);
          setAddressId('');
          setSearchQuery('');
          actions.resetForm();
        });
      },
      onError: (e) => {
        const error = e as Error;
        const error_messge_id = pushNotification(`${error.message}`, {
          variant: 'error',
          onClick: () => {
            actions.submitForm();
            dismissNotification(error_messge_id);
          },
          autoHideDuration: 10000
        });
      },
      onSettled: () => {
        dismissNotification(loading);
        actions.setSubmitting(false);
      }
    });
  };

  const handleEdit = (
    values: LocationFormValues,
    actions: FormikHelpers<LocationFormValues>
  ) => {
    const payload: LocationPayload = {
      address_id: addressId || (addresses?.data[0].address.id as string),
      campus: values.name,
      delivery_instructions: values.delivery_instructions,
      floor: values.unit_number,
      state: values.state
    };

    const loading = pushNotification(`Updating ${payload.campus}...`);

    edit.mutate(
      { id: id, payload },
      {
        onSuccess: () => {
          queryClient.refetchQueries(LocationQueryKeys.detail(id));

          queryClient.invalidateQueries(LocationQueryKeys.list()).then(() => {
            pushNotification(`${payload.campus} updated`, {
              variant: 'success'
            });
            setAction(Actions.Edit, false);
            setAddressId('');
            setSearchQuery('');
            actions.resetForm();
          });
        },
        onError: (e) => {
          const error = e as Error;
          const error_messge_id = pushNotification(`${error.message}`, {
            variant: 'error',
            onClick: () => {
              actions.submitForm();
              dismissNotification(error_messge_id);
            },
            autoHideDuration: 10000
          });
        },
        onSettled: () => {
          dismissNotification(loading);
          actions.setSubmitting(false);
        }
      }
    );
  };

  // todo: change this into a hook
  const onPageChange = (
    changes: Record<string, string | number | undefined>
  ) => {
    let params = {
      ...queryParam,
      ...changes
    };

    params = Object.keys(params).reduce(
      (acc, key) => {
        params[key] !== undefined && (acc[key] = params[key]);
        return acc;
      },
      {} as Record<string, any>
    );

    navigate(
      {
        pathname: AppRoute.Location,
        search: generateQuery(params)
      },
      { replace: true }
    );
  };

  const locationListQuery = useQueryWrapperP0(
    LocationQueryKeys.list(filtered),
    () => api.company.location.list(filtered)
  );
  const deleteLocation = useMutation(async (id: number) => {
    return api.company.location.delete(id);
  });

  const handleSearch = (value: string) => {
    onPageChange({ search: value || undefined, offset: undefined });
    return;
  };

  const handlePaginationChange = (page: number, rowsPerPage: number) => {
    onPageChange({
      ...{
        offset: page ? page * rowsPerPage : undefined,
        count: rowsPerPage
      }
    });
  };

  const page = queryParam.offset ? parseInt(queryParam.offset) : undefined;
  const rowsPerPage = queryParam.count ? parseInt(queryParam.count) : undefined;
  const tableListProps = {
    ...(page && {
      page: Math.floor(page / (rowsPerPage || RowsPerPage.TWENTYFIVE))
    }),
    ...(rowsPerPage && { rowsPerPage })
  };

  const locationList = locationListQuery.data?.data || [];
  const locations = locationToLocationListInfo(locationList);
  const initialValuesEdit = location.data
    ? {
        ...location.data,
        geolocatorStreet: {
          id: -1,
          name: location.data.street_and_building
        }
      }
    : initialValues;

  const handleDeleteRow = (row: LocationListInfo) => {
    return () => {
      setLocationName(row.name);
      handleClick(Actions.Delete, row.id.toString());
    };
  };

  const handleDelete = () => {
    deleteLocation.mutate(id, {
      onSuccess: () => {
        queryClient.invalidateQueries(LocationQueryKeys.list()).then(() => {
          pushNotification(`${locationName} deleted!`, {
            variant: 'success'
          });
        });
      },
      onError: (e) => {
        const error = e as Error;
        const error_message_id = pushNotification(`${error.message}`, {
          variant: 'error',
          onClick: () => {
            dismissNotification(error_message_id);
          },
          autoHideDuration: 10000
        });
      }
    });
  };

  const LocationOptions =
    addresses?.data.map((location) => {
      return {
        id: location.address.id,
        name: location.address.formatted_address
      } as AutocompleteOption;
    }) || [];

  const detailFormRef = useRef<FormikProps<LocationFormValues>>(null);

  return (
    <Page
      actions={{
        [Actions.Delete]: {
          component: <DeleteDialogBtn onClick={handleDelete} />,
          type: ActionType.AppDialog,
          options: {
            title: `Delete ${locationName}`,
            content: deleteDialogContent
          }
        },
        [Actions.Edit]: {
          component: !location.isLoading ? (
            <LocationForm
              cities={cities.data || []}
              onSubmit={handleEdit}
              searchQuery={searchQuery}
              onQuery={(value: string) => setSearchQuery(value)}
              initialValues={initialValuesEdit}
              locations={LocationOptions}
              onSelectAddress={handleSelectAddress}
              loadingLocations={isAddressesLoading}
              detailFormRef={detailFormRef}
              buttons={() => (
                <div style={{ width: '100%' }}>
                  <Button
                    key="1"
                    type="submit"
                    size="small"
                    layout="full-width-primary"
                    disabled={detailFormRef.current?.isSubmitting}
                    onClick={() => {
                      detailFormRef.current?.handleSubmit();
                    }}
                  >
                    {ButtonText.Save}
                  </Button>
                </div>
              )}
            />
          ) : (
            <LoadingAnimation />
          ),
          type: ActionType.RightDrawer,
          options: {
            title: edit_title
          }
        },
        [Actions.Add]: {
          component: (
            <LocationForm
              cities={cities.data || []}
              onSubmit={handleCreate}
              initialValues={initialValues}
              searchQuery={searchQuery}
              onQuery={(value: string) => setSearchQuery(value)}
              onSelectAddress={handleSelectAddress}
              locations={LocationOptions}
              detailFormRef={detailFormRef}
              loadingLocations={isAddressesLoading}
              buttons={() => (
                <>
                  <Button
                    key="1"
                    disabled={detailFormRef.current?.isSubmitting}
                    size="small"
                    style={{ width: '160px' }}
                    type="submit"
                    variant="secondary"
                    onClick={() => {
                      setSidebarOpen(true);
                      detailFormRef.current?.handleSubmit();
                    }}
                  >
                    {ButtonText.SaveAndNext}
                  </Button>
                  <Button
                    key="2"
                    disabled={detailFormRef.current?.isSubmitting}
                    size="small"
                    style={{ width: '160px' }}
                    type="submit"
                    onClick={() => {
                      setSidebarOpen(false);
                      detailFormRef.current?.handleSubmit();
                    }}
                  >
                    {ButtonText.Save}
                  </Button>
                </>
              )}
            />
          ),
          type: ActionType.RightDrawer,
          options: {
            title: add_title
          }
        }
      }}
    >
      <PageHeader
        title={page_title}
        subtitle={<>{page_subtitle}</>}
        buttons={[
          <Button
            key="1"
            onClick={() => {
              setSearchQuery('');
              handleClick(Actions.Add);
            }}
            data-testid={TestID.addButton}
          >
            Add a location
          </Button>
        ]}
      />
      <TableList
        rowsPerPage={rowsPerPage}
        data={locations}
        columns={columns}
        loading={locationListQuery.isLoading}
        onPaginationChange={handlePaginationChange}
        {...tableListProps}
        empty={
          Object.keys(filtered).length == 0 && (
            <CTA
              illustration={isFoodora() ? FoodoraPermissions : Permissions}
              title={cta_title}
              subtitle={cta_subtitle}
            >
              <Button
                key="1"
                onClick={() => {
                  setSearchQuery('');
                  handleClick(Actions.Add);
                }}
                data-testid={TestID.addButtonCTA}
              >
                Add a location
              </Button>
            </CTA>
          )
        }
        action={(row: LocationListInfo) => (
          <ActionMenu>
            <MenuItem onClick={onEdit(row.id)} style={{ minWidth: 200 }}>
              Edit
            </MenuItem>
            <MenuItem onClick={handleDeleteRow(row)} style={{ minWidth: 200 }}>
              Delete
            </MenuItem>
          </ActionMenu>
        )}
        total={
          locationListQuery.error
            ? 0
            : locationListQuery.data
              ? locationListQuery.data.total
              : -1
        }
      >
        <TableFilterRow>
          <SearchInput
            value={queryParam.search || ''}
            onSearch={handleSearch}
            placeholder={'Search for location name, street/building or zipcode'}
            sx={{ width: 480 }}
          />
        </TableFilterRow>
      </TableList>
    </Page>
  );
}
